/**
 * Employment equity — race and gender against the grade bands.
 *
 * Two things drive this layout. First, the board question is not "what is the
 * overall mix" but "does the mix hold at the top", so every headline tile
 * carries the all-employee figure AND the senior (D+) figure with the gap
 * between them spelled out — the gap is the finding, not either number alone.
 * Second, the bands are drawn senior-first so the eye starts where the scrutiny
 * is rather than at the largest band. Bands D, E and F arrive as ONE senior
 * column ("D-F"): split finer, the smallest senior bands would identify people.
 *
 * SUPPRESSION IS DRAWN, NOT DROPPED. A cell identity withholds arrives as null,
 * and so can a band total when it would let a small group be worked out.
 * Dropping the null cells and rescaling on the published ones would silently
 * overstate every group that survived. So the people outside the published
 * cells — the withheld and the not classified — are drawn as a hatched remainder
 * inside each 100% stack, sized as the band total less the published cells. The
 * remainder is never PRINTED as a count: it can be a group below five that the
 * panel withholds elsewhere. A band whose total is withheld draws no split and
 * lists the cells that are still published.
 *
 * NULL MEANS WITHHELD; A MISSING KEY MEANS NOT AVAILABLE FOR THIS DATE. A null
 * never reaches an arithmetic sum or a Math.max here, and no withheld figure is
 * worked out from its neighbours.
 *
 * Deliberately not a recharts panel: the stack needs a segment that stands for
 * "withheld", a denominator taken from the band total rather than from the
 * segments, and a total column that can say so. The house BarRow grid gives all
 * three; a stacked bar chart would have to fake them.
 */
import React from 'react';
import { MetricTile, TileRow } from './MetricTile';
import { PanelShell, PanelSkeleton, PanelUnavailable } from './PanelShell';
import {
  Def,
  basisLabel,
  fmtNum,
  fmtPct,
  panelDateLabel,
  readExtras,
  type DefLookup,
  type WorkforceBasis,
  type WorkforcePanels,
} from './apiContract';

interface Props {
  panels: WorkforcePanels | null;
  basis: WorkforceBasis;
  loading: boolean;
  def: DefLookup;
}

/** A served figure: a number, null (withheld), or undefined (not carried for this date). */
type Cell = number | null | undefined;
type Matrix = Record<string, Record<string, number | null>>;

// =============================================================================
// PALETTE — hard-coded hex, same family as apiContract CATEGORY_COLORS
// =============================================================================

const RACE_ORDER = ['African', 'Coloured', 'Indian', 'White'];
const RACE_COLORS: Record<string, string> = {
  African: '#15305D',
  Coloured: '#2E6FD9',
  Indian: '#0B7F72',
  White: '#CFAD80',
};
const GENDER_ORDER = ['Female', 'Male'];
const GENDER_COLORS: Record<string, string> = {
  Female: '#2E6FD9',
  Male: '#15305D',
};
const OTHER_COLOR = '#B9C4D6';
/** Hatched, so a withheld remainder never reads as just another group. */
const WITHHELD_FILL = 'repeating-linear-gradient(45deg, #C3CDDD 0 3px, #EDF1F7 3px 6px)';

/** Senior first — the senior group, served as one column — then down, non-graded last.
 *  F, E and D keep their place for a copy that carries them separately. */
const BAND_DISPLAY_ORDER = ['D-F', 'F', 'E', 'D', 'C', 'B', 'NG'];
const SENIOR_COLUMN = 'D-F';
const FALLBACK_SENIOR_BANDS = ['D', 'E', 'F'];

const WITHHELD = 'withheld to protect small groups';
const NOT_AVAILABLE = 'not available for this date';
const WITHHELD_TIP =
  'Withheld to protect small groups: fewer than five people, or a figure that would let such a ' +
  'group be worked out. They are still inside the totals.';

// =============================================================================
// HELPERS
// =============================================================================

/** Server definition wins; the local text is only the floor, so a tooltip is
 *  never empty for a key nobody has published a definition for yet. */
const tip = (def: DefLookup, k: string, fallback: string) => def(k) ?? fallback;
/** Same rule for MetricTile, whose `definition` prop beats the lookup. */
const localDef = (def: DefLookup, k: string, text: string) => (def(k) ? undefined : text);

const orderKeys = (available: string[], preferred: string[]) => [
  ...preferred.filter((k) => available.includes(k)),
  ...available.filter((k) => !preferred.includes(k)),
];

const capitalise = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

const bandName = (band: string) => (band === SENIOR_COLUMN ? 'D–F' : band);

const bandCaption = (band: string, senior: boolean) =>
  band === 'NG' ? 'non-graded' : senior ? 'senior' : '';

/** A count identity withholds only below five, in the words of a sentence. */
const smallCount = (v: Cell) =>
  v === undefined ? NOT_AVAILABLE : v === null ? 'fewer than five' : fmtNum(v);

/** A served figure: null reads withheld, a missing key reads not available for this date. */
const Figure: React.FC<{ v: Cell; fmt?: (n: number) => string; tile?: boolean }> = ({
  v,
  fmt = fmtNum,
  tile,
}) => {
  if (typeof v === 'number') return <>{fmt(v)}</>;
  const words = v === null ? WITHHELD : NOT_AVAILABLE;
  return (
    <span
      className={
        tile ? 'block text-[12px] font-medium leading-snug text-gray-400' : 'font-normal text-gray-500'
      }
      title={v === null ? WITHHELD_TIP : undefined}
    >
      {tile ? capitalise(words) : words}
    </span>
  );
};

/** One word for the narrow right-hand column, where a figure is not shown. */
const ShortGap: React.FC<{ v: null | undefined; title?: string }> = ({ v, title }) => (
  <span
    className="text-[11px] font-normal text-gray-400"
    title={title ?? (v === null ? WITHHELD_TIP : 'Not available for this date.')}
  >
    {v === null ? 'Withheld' : 'Not available'}
  </span>
);

/** The gap between the senior figure and the all-employee figure, in points. */
function gapText(seniorPct: Cell, overallPct: Cell): string | null {
  if (typeof seniorPct !== 'number' || typeof overallPct !== 'number') return null;
  const d = seniorPct - overallPct;
  if (Math.abs(d) < 0.05) return 'level with all employees';
  return `${Math.abs(d).toFixed(1)} pts ${d < 0 ? 'lower' : 'higher'} than all employees`;
}

// =============================================================================
// STACKED ROW — the BarRow grid, with segments instead of one bar
// =============================================================================

interface Seg {
  key: string;
  pct: number;
  color?: string;
  hatched?: boolean;
  title: string;
}

const StackRow: React.FC<{
  label: React.ReactNode;
  segs: Seg[];
  right: React.ReactNode;
  rightSub?: React.ReactNode;
  height?: number;
}> = ({ label, segs, right, rightSub, height = 15 }) => (
  <div
    className="grid items-center gap-2.5 py-[3px] text-[12.5px]"
    style={{ gridTemplateColumns: 'minmax(0, 118px) 1fr 92px' }}
  >
    <div className="truncate text-gray-700">{label}</div>
    <div className="flex overflow-hidden rounded-sm bg-gray-100" style={{ height }}>
      {segs
        .filter((s) => s.pct > 0)
        .map((s) => (
          <span
            key={s.key}
            title={s.title}
            className="shrink-0"
            style={{
              width: `${s.pct}%`,
              minWidth: 2,
              background: s.hatched ? WITHHELD_FILL : s.color,
            }}
          />
        ))}
    </div>
    <div className="text-right leading-tight">
      <div className="font-semibold tabular-nums text-heritage-blue">{right}</div>
      {rightSub ? <div className="text-[10px] font-normal text-gray-400">{rightSub}</div> : null}
    </div>
  </div>
);

interface BandStack {
  segs: Seg[];
  /** How many cells in this band are withheld. */
  hidden: number;
  /** True when the band total itself is withheld: no denominator exists. */
  totalWithheld: boolean;
  /** Cells still published in a band whose total is withheld: listed as counts, never as shares. */
  publishedCells: { key: string; count: number }[];
}

/**
 * One band 100% stack. The denominator is the BAND TOTAL, not the sum of the
 * published cells: the difference between the two is the withheld and not
 * classified remainder, and it gets drawn — never counted out loud.
 */
function buildBandStack(
  matrix: Matrix,
  keys: string[],
  band: string,
  total: Cell,
  colorFor: (k: string) => string,
): BandStack {
  let published = 0;
  let hidden = 0;
  const cells: { key: string; count: number }[] = [];
  const name = bandName(band);

  keys.forEach((k) => {
    const v: Cell = matrix[k]?.[band];
    // null is WITHHELD: counted as hidden, never added.
    if (typeof v !== 'number') {
      if (v === null) hidden += 1;
      return;
    }
    published += v;
    cells.push({ key: k, count: v });
  });

  if (total === null) {
    return {
      segs: [
        {
          key: 'withheld-total',
          pct: 100,
          hatched: true,
          title:
            `Band ${name}: the band total is withheld to protect small groups, so no split is ` +
            'drawn. These people are still counted in the panel population.',
        },
      ],
      hidden,
      totalWithheld: true,
      publishedCells: cells,
    };
  }
  if (total === undefined) {
    return { segs: [], hidden, totalWithheld: false, publishedCells: [] };
  }

  const denom = Math.max(total, published);
  if (denom <= 0) {
    return { segs: [], hidden, totalWithheld: false, publishedCells: [] };
  }
  const remainder = Math.max(0, total - published);

  const segs: Seg[] = cells
    .filter((c) => c.count > 0)
    .map((c) => ({
      key: c.key,
      pct: (c.count / denom) * 100,
      color: colorFor(c.key),
      title: `${c.key}: ${fmtNum(c.count)} of ${fmtNum(total)} in band ${name} (${fmtPct(
        (c.count / denom) * 100,
      )})`,
    }));

  if (remainder > 0) {
    segs.push({
      key: 'withheld',
      pct: (remainder / denom) * 100,
      hatched: true,
      title:
        hidden > 0
          ? `People in band ${name} who are withheld to protect small groups or have no recorded ` +
            'classification. The band total shown still includes them.'
          : `People in band ${name} with no recorded classification. The band total shown still ` +
            'includes them.',
    });
  }

  return { segs, hidden, totalWithheld: false, publishedCells: [] };
}

const Swatch: React.FC<{
  label: string;
  color?: string;
  hatched?: boolean;
  defKey: string;
  fallback: string;
  def: DefLookup;
}> = ({ label, color, hatched, defKey, fallback, def }) => (
  <span className="inline-flex items-center gap-1.5 text-[11px] text-gray-600">
    <span
      className="inline-block h-2.5 w-2.5 rounded-sm"
      style={{ background: hatched ? WITHHELD_FILL : color }}
    />
    {label}
    <Def def={def} k={defKey} text={tip(def, defKey, fallback)} />
  </span>
);

const NotAvailableNote: React.FC<{ what: string }> = ({ what }) => (
  <div className="rounded border border-gray-200 bg-gray-50 p-2.5 text-[11px] leading-snug text-gray-600">
    {what} is not available for this date.
  </div>
);

const SHARE_TILES = [
  {
    label: 'Women',
    count: 'female',
    pct: 'female_pct',
    defKey: 'equity_women',
    text: 'Women as a percentage of the employees on this panel.',
  },
  {
    label: 'African',
    count: 'african',
    pct: 'african_pct',
    defKey: 'equity_african',
    text: 'Employees recorded as African, as a percentage of the employees on this panel.',
  },
  {
    label: 'Designated (ACI)',
    count: 'designated_ACI',
    pct: 'designated_pct',
    defKey: 'equity_designated',
    text:
      'African, Coloured and Indian employees together, as a percentage of the employees on this ' +
      'panel — the designated-group measure the equity plan reports on.',
  },
] as const;

// =============================================================================
// PANEL
// =============================================================================

export const EquityPanel: React.FC<Props> = ({ panels, basis, loading, def }) => {
  const equity = readExtras(panels, 'equity');
  const shares = equity?.shares;
  const senior = equity?.senior;
  const population = equity?.population;

  const raceMatrix: Matrix = equity?.race_x_band ?? {};
  const genderMatrix: Matrix = equity?.gender_x_band ?? {};
  const bandTotals: Record<string, number | null> = equity?.band_totals ?? {};

  const apiBands = equity?.bands?.length ? equity.bands : Object.keys(bandTotals);
  const bands = orderKeys(apiBands, BAND_DISPLAY_ORDER);

  // Key names only: a senior band's own count can be withheld.
  const seniorBandKeys = Object.keys(senior?.by_band ?? {});
  const seniorBands = new Set(seniorBandKeys.length ? seniorBandKeys : FALLBACK_SENIOR_BANDS);
  const isSenior = (band: string) => band === SENIOR_COLUMN || seniorBands.has(band);

  const races = orderKeys(Object.keys(raceMatrix), RACE_ORDER);
  const genders = orderKeys(Object.keys(genderMatrix), GENDER_ORDER);

  const stacksFor = (matrix: Matrix, keys: string[], colors: Record<string, string>) =>
    bands.map((b) => {
      const total: Cell = bandTotals[b];
      return {
        band: b,
        total,
        ...buildBandStack(matrix, keys, b, total, (k) => colors[k] ?? OTHER_COLOR),
      };
    });
  const raceStacks = stacksFor(raceMatrix, races, RACE_COLORS);
  const genderStacks = stacksFor(genderMatrix, genders, GENDER_COLORS);

  const raceHidden = raceStacks.reduce((s, r) => s + r.hidden, 0);
  const raceCells = races.length * bands.length;
  const bandsFullyWithheld = raceStacks.filter((r) => r.totalWithheld).map((r) => bandName(r.band));

  const raceUnknown: Cell = shares?.race_unknown;
  const genderUnknown: Cell = shares?.gender_unknown;
  const notInCatalogue: Cell = equity?.role_not_in_catalogue;

  // The bridge from employees in post to this population, from this block alone. A withheld
  // count stays withheld: it is never worked out as the difference of the two totals.
  const inPost: Cell = equity?.employees_in_post;
  const notEmployed: Cell = equity?.in_post_not_employed;
  const populationSentence =
    typeof inPost === 'number' && typeof population === 'number' && inPost > population ? (
      <>
        {' '}
        The {fmtNum(inPost)} employees in post also include{' '}
        {typeof notEmployed === 'number'
          ? `${fmtNum(notEmployed)} ${notEmployed === 1 ? 'person' : 'people'}`
          : notEmployed === null
            ? 'fewer than five people'
            : 'people'}{' '}
        who still hold a position but whose worker record no longer shows them as employed.
      </>
    ) : null;

  const bandLabel = (band: string) => (
    <span className="flex items-baseline gap-1.5">
      <span className="font-semibold text-heritage-blue">{bandName(band)}</span>
      <span className="truncate text-[10px] uppercase tracking-wide text-gray-400">
        {bandCaption(band, isSenior(band))}
      </span>
    </span>
  );

  const publishedLine = (row: { band: string } & BandStack) =>
    row.totalWithheld && row.publishedCells.length ? (
      <div className="pl-[128px] text-[10.5px] leading-snug text-gray-500">
        Shown: {row.publishedCells.map((c) => `${c.key} ${fmtNum(c.count)}`).join(' · ')}. The rest
        of band {bandName(row.band)} is {WITHHELD}.
      </div>
    ) : null;

  return (
    <PanelShell
      title="Employment equity"
      defKey="equity"
      def={def}
      window={
        <>
          {equity ? <>{panelDateLabel(panels, 'equity')}. </> : null}
          Employees in post (permanent and fixed-term) whose worker record still shows them as
          employed. Grade bands follow the current role catalogue. It does <b>not</b> follow the
          basis toggle
          {basis !== 'employees' ? (
            <> — you are viewing the {basisLabel(basis).toLowerCase()} basis above</>
          ) : null}{' '}
          or the period picker.
        </>
      }
      footnote={
        equity ? (
          <>
            Population: <b>{fmtNum(population)}</b> employees in post whose worker record still shows
            them as employed.{populationSentence} Declared disability:{' '}
            <b>{smallCount(equity.disability_flagged)}</b>. Foreign nationals:{' '}
            <b>{smallCount(equity.foreign_nationals)}</b>. Both are floors rather than counts:
            disability is self-declared, and until 28 July 2026 the onboarding system recorded every
            new hire as not disabled; the foreign-national figure comes from the recorded
            nationality, so anyone whose nationality is blank falls outside it
            {equity.nationality_not_captured !== 0 ? (
              <> (nationality not recorded: {smallCount(equity.nationality_not_captured)})</>
            ) : null}
            .{' '}
            {raceUnknown === 0 && genderUnknown === 0
              ? 'No employee in this population is missing a race or gender classification.'
              : `Race not classified: ${smallCount(raceUnknown)}. Gender not classified: ${smallCount(
                  genderUnknown,
                )}.`}
          </>
        ) : undefined
      }
    >
      {loading && !panels ? (
        <PanelSkeleton height={420} />
      ) : !panels ? (
        <PanelUnavailable what="The employment-equity view" />
      ) : !equity ? (
        <NotAvailableNote what="The employment-equity view" />
      ) : (
        <div className="space-y-5">
          {/* ── headline: overall vs senior, with the gap named ─────────── */}
          <TileRow min="200px">
            {SHARE_TILES.map((t) => {
              const gap = gapText(senior?.[t.pct], shares?.[t.pct]);
              const count: Cell = shares?.[t.count];
              return (
                <MetricTile
                  key={t.label}
                  label={t.label}
                  value={<Figure v={shares?.[t.pct]} fmt={fmtPct} tile />}
                  defKey={t.defKey}
                  def={def}
                  definition={localDef(def, t.defKey, t.text)}
                  sub={
                    <>
                      {typeof count === 'number' ? (
                        <>
                          {fmtNum(count)} of {fmtNum(population)} employees
                        </>
                      ) : null}
                      <div>
                        Senior D+: <b><Figure v={senior?.[t.pct]} fmt={fmtPct} /></b>
                        {gap ? ` — ${gap}` : ''}
                      </div>
                    </>
                  }
                />
              );
            })}
            <MetricTile
              label="Senior population (D+)"
              value={<Figure v={senior?.people} tile />}
              defKey="equity_senior_population"
              def={def}
              definition={localDef(
                def,
                'equity_senior_population',
                'Employees in grade bands D and above — the population every senior figure on this ' +
                  'panel is measured against.',
              )}
              sub={
                <>
                  Bands {[...seniorBands].sort().join(', ')}
                  {typeof senior?.people === 'number' && typeof population === 'number' && population > 0 ? (
                    <div>{fmtPct((senior.people / population) * 100)} of all employees</div>
                  ) : null}
                </>
              }
            />
          </TileRow>

          {/* ── race x band ─────────────────────────────────────────────── */}
          <div>
            <div className="mb-1.5 flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
              <div className="flex items-center text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                Race by grade band — senior bands first
                <Def
                  def={def}
                  k="equity_race_x_band"
                  text={tip(
                    def,
                    'equity_race_x_band',
                    'Each band is drawn as 100% of its own total. Bands D, E and F are shown together ' +
                      'as the senior group; B and NG (non-graded) follow.',
                  )}
                />
              </div>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                {races.map((r) => (
                  <Swatch
                    key={r}
                    label={r}
                    color={RACE_COLORS[r] ?? OTHER_COLOR}
                    defKey={`equity_race_${r.toLowerCase()}`}
                    fallback={`Employees classified ${r} on the F&O worker record.`}
                    def={def}
                  />
                ))}
                <Swatch
                  label="Withheld or not classified"
                  hatched
                  defKey="small_cell_suppression"
                  fallback={WITHHELD_TIP}
                  def={def}
                />
              </div>
            </div>

            {raceStacks.map((row) => (
              <React.Fragment key={row.band}>
                <StackRow
                  label={bandLabel(row.band)}
                  segs={row.segs}
                  right={
                    typeof row.total === 'number' ? (
                      <span title={`${fmtNum(row.total)} employees in band ${bandName(row.band)}`}>
                        {fmtNum(row.total)}
                      </span>
                    ) : (
                      <ShortGap v={row.total} />
                    )
                  }
                  rightSub={
                    row.hidden > 0 ? (
                      <span
                        title={
                          row.totalWithheld
                            ? `Band ${bandName(row.band)} is withheld in full: the band total is withheld.`
                            : `${row.hidden} of ${races.length} race figures in band ${bandName(row.band)} ` +
                              `are ${WITHHELD}.`
                        }
                      >
                        {row.hidden} withheld
                      </span>
                    ) : null
                  }
                />
                {publishedLine(row)}
              </React.Fragment>
            ))}

            <div className="mt-1.5 text-[11px] leading-snug text-gray-500">
              {raceHidden > 0
                ? `${raceHidden} of the ${raceCells} race-by-band figures are ${WITHHELD} (fewer than ` +
                  'five people, or needed to keep such a group from being worked out). '
                : null}
              The hatched remainder holds{raceHidden > 0 ? ' those people and' : null} anyone whose race
              is not recorded, so every bar is 100% of its band and each band total still includes
              them.
              {bandsFullyWithheld.length ? (
                <>
                  {' '}
                  Band{bandsFullyWithheld.length > 1 ? 's' : ''} {bandsFullyWithheld.join(' and ')}{' '}
                  {bandsFullyWithheld.length > 1 ? 'are' : 'is'} withheld in full: the band total is
                  withheld, so no split is drawn.
                </>
              ) : null}
              {typeof notInCatalogue === 'number' && notInCatalogue > 0 ? (
                <>
                  {' '}
                  Not graded includes {fmtNum(notInCatalogue)}{' '}
                  {notInCatalogue === 1 ? 'person' : 'people'} whose role is not yet in the role
                  catalogue.
                </>
              ) : notInCatalogue === null ? (
                <> Not graded includes fewer than five people whose role is not yet in the role catalogue.</>
              ) : notInCatalogue === undefined ? (
                <>
                  {' '}
                  How many not-graded people hold a role not yet in the role catalogue is {NOT_AVAILABLE}.
                </>
              ) : null}
            </div>
          </div>

          {/* ── gender x band ───────────────────────────────────────────── */}
          <div>
            <div className="mb-1.5 flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
              <div className="flex items-center text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                Gender by grade band
                <Def
                  def={def}
                  k="equity_gender_x_band"
                  text={tip(
                    def,
                    'equity_gender_x_band',
                    'The same bands split by gender. The right-hand figure is women as a share of ' +
                      'women plus men in the band.',
                  )}
                />
              </div>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                {genders.map((g) => (
                  <Swatch
                    key={g}
                    label={g}
                    color={GENDER_COLORS[g] ?? OTHER_COLOR}
                    defKey={`equity_gender_${g.toLowerCase()}`}
                    fallback={`Employees recorded as ${g} on the F&O worker record.`}
                    def={def}
                  />
                ))}
                <Swatch
                  label="Withheld or not classified"
                  hatched
                  defKey="small_cell_suppression"
                  fallback={WITHHELD_TIP}
                  def={def}
                />
              </div>
            </div>

            {genderStacks.map((row) => {
              const femaleCell: Cell = genderMatrix.Female?.[row.band];
              const maleCell: Cell = genderMatrix.Male?.[row.band];
              // Women of women plus men, as the board pack computes it. A withheld cell on
              // either side yields no percentage — never a 0%, never a share worked out
              // from the band total.
              const femalePct =
                typeof femaleCell === 'number' && typeof maleCell === 'number' && femaleCell + maleCell > 0
                  ? (femaleCell / (femaleCell + maleCell)) * 100
                  : null;
              const shareGap =
                femaleCell === null || maleCell === null
                  ? null
                  : femaleCell === undefined || maleCell === undefined
                    ? undefined
                    : 'none';
              return (
                <React.Fragment key={row.band}>
                  <StackRow
                    height={10}
                    label={bandLabel(row.band)}
                    segs={row.segs}
                    right={
                      femalePct !== null ? (
                        <span
                          title={`${fmtNum(femaleCell)} women of ${fmtNum(
                            (femaleCell as number) + (maleCell as number),
                          )} women and men in band ${bandName(row.band)}`}
                        >
                          {fmtPct(femalePct)}
                        </span>
                      ) : shareGap === 'none' ? (
                        <span title="No employee in this band has a recorded gender.">—</span>
                      ) : (
                        <ShortGap v={shareGap} />
                      )
                    }
                    rightSub={<span className="uppercase tracking-wide">female</span>}
                  />
                  {publishedLine(row)}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      )}
    </PanelShell>
  );
};
